
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { 
  BadgeDollarSign, 
  FileSearch, 
  FileText, 
  HelpCircle, 
  Info, 
  MailOpen, 
  ArrowRight, 
  ExternalLink,
  BookText
} from 'lucide-react';

// Sample unclaimed royalties data
const unclaimedRoyaltiesData = [
  { 
    id: 1, 
    source: 'SoundExchange', 
    potentialAmount: '$345.80', 
    status: 'unclaimed', 
    lastUpdated: '2023-12-10',
    details: 'Digital performance royalties for satellite radio and streaming services'
  },
  { 
    id: 2, 
    source: 'Music Licensing Agency', 
    potentialAmount: '$128.45', 
    status: 'pending verification', 
    lastUpdated: '2024-01-15',
    details: 'Public performance royalties from international sources'
  },
  { 
    id: 3, 
    source: 'MLC', 
    potentialAmount: '$217.30', 
    status: 'unclaimed', 
    lastUpdated: '2024-02-20',
    details: 'Mechanical royalties from digital streaming platforms'
  }
];

// Guide steps for claiming royalties
const claimingSteps = [
  {
    title: 'Research Potential Sources',
    description: 'Identify organizations that might be holding your unclaimed royalties.',
    icon: <FileSearch className="w-5 h-5 text-emerald-500" />,
    sources: [
      { name: 'SoundExchange', link: 'https://www.soundexchange.com/' },
      { name: 'Mechanical Licensing Collective (MLC)', link: 'https://www.themlc.com/' },
      { name: 'ASCAP/BMI/SESAC', link: '#' },
      { name: 'International Collection Societies', link: '#' }
    ]
  },
  {
    title: 'Verify Your Identity',
    description: 'Prepare documentation to prove ownership of your musical works.',
    icon: <FileText className="w-5 h-5 text-emerald-500" />,
    documents: [
      'Government-issued ID',
      'Tax information (W-9 or equivalent)',
      'List of your registered works',
      'Proof of rights ownership'
    ]
  },
  {
    title: 'Submit Claims',
    description: 'Contact each organization and follow their specific claim process.',
    icon: <MailOpen className="w-5 h-5 text-emerald-500" />
  },
  {
    title: 'Track Progress',
    description: 'Monitor the status of your claims and follow up as needed.',
    icon: <BookText className="w-5 h-5 text-emerald-500" />
  }
];

const UnclaimedRoyalties: React.FC = () => {
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  
  return (
    <Card variant="sunset" className="overflow-hidden">
      <CardHeader>
        <CardTitle className="text-xl flex items-center gap-2 text-white">
          <BadgeDollarSign className="h-5 w-5 text-white/80" />
          Unclaimed Royalties
        </CardTitle>
        <CardDescription className="text-white/80">
          Track and claim your potential unclaimed royalties from various sources
        </CardDescription>
      </CardHeader>
      
      <CardContent>
        <div className="space-y-6">
          <div className="bg-white/10 backdrop-blur-sm p-4 rounded-lg border border-white/20">
            <div className="flex items-start gap-3 mb-4">
              <Info className="w-5 h-5 text-white/80 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-white">What are unclaimed royalties?</h3>
                <p className="text-xs text-white/80 mt-1">
                  Unclaimed royalties (often called "black box royalties") are music royalties that have been collected 
                  by rights organizations but haven't been distributed to the rightful copyright owners due to 
                  missing or incorrect metadata, unregistered works, or other issues.
                </p>
              </div>
            </div>
            
            <Button 
              variant="glass" 
              size="sm" 
              className="gap-1 w-full sm:w-auto"
              onClick={() => setIsGuideOpen(true)}
            >
              <HelpCircle className="w-4 h-4" />
              <span>How to Claim Your Royalties</span>
            </Button>
          </div>
          
          <div className="bg-white/5 backdrop-blur-sm rounded-lg border border-white/10 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-white/5 border-white/10">
                  <TableHead className="text-white">Source</TableHead>
                  <TableHead className="text-white">Potential Amount</TableHead>
                  <TableHead className="text-white">Status</TableHead>
                  <TableHead className="text-white">Last Updated</TableHead>
                  <TableHead className="text-white">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {unclaimedRoyaltiesData.map((royalty) => (
                  <TableRow key={royalty.id} className="hover:bg-white/5 border-white/10">
                    <TableCell className="font-medium text-white">
                      <div className="flex flex-col">
                        <span>{royalty.source}</span>
                        <span className="text-xs text-white/70">{royalty.details}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-white font-medium">{royalty.potentialAmount}</TableCell>
                    <TableCell>
                      <Badge 
                        variant={royalty.status === 'unclaimed' ? 'outline' : 'default'}
                        className={`
                          ${royalty.status === 'unclaimed' 
                            ? 'border-orange-400 text-orange-400' 
                            : 'bg-yellow-500/20 text-yellow-400 border-yellow-400/30'}
                        `}
                      >
                        {royalty.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-white/70">{royalty.lastUpdated}</TableCell>
                    <TableCell>
                      <Button variant="glass" size="sm" className="gap-1">
                        <span>Claim</span>
                        <ArrowRight className="w-3 h-3" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          
          <div className="flex justify-end">
            <Button variant="glass" size="sm">Scan for More Unclaimed Royalties</Button>
          </div>
        </div>
        
        <Dialog open={isGuideOpen} onOpenChange={setIsGuideOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>How to Claim Your Unclaimed Royalties</DialogTitle>
              <DialogDescription>
                Follow these steps to identify and claim your unclaimed royalties from various sources.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {claimingSteps.map((step, index) => (
                <div key={index} className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    {step.icon}
                  </div>
                  
                  <div className="flex-1">
                    <h3 className="text-base font-medium flex items-center gap-2">
                      <span>Step {index + 1}: {step.title}</span>
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">{step.description}</p>
                    
                    {step.sources && (
                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {step.sources.map((source, i) => (
                          <a 
                            key={i} 
                            href={source.link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-sm text-primary hover:underline"
                          >
                            {source.name}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        ))}
                      </div>
                    )}
                    
                    {step.documents && (
                      <ul className="mt-2 space-y-1">
                        {step.documents.map((doc, i) => (
                          <li key={i} className="text-sm text-muted-foreground flex items-center gap-1.5">
                            <div className="w-1 h-1 rounded-full bg-primary"></div>
                            {doc}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              ))}
              
              <div className="rounded-md bg-muted p-4 mt-6">
                <div className="flex gap-2">
                  <Info className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                  <div>
                    <h4 className="text-sm font-medium">Professional Assistance</h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      Consider working with a royalty collection specialist or music rights lawyer to maximize your recovery,
                      especially for complex cases or significant amounts.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsGuideOpen(false)}>Close</Button>
              <Button>Save as PDF</Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default UnclaimedRoyalties;
